import { highlightLabel } from '@magicschool/spellbook/lib/autocomplete';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useMemo } from 'react';

export interface UseAutocompleteHighlightedLabelOptions {
  /**
   * If true, disables text highlighting. Useful for cases where you want to display the label without any highlights.
   */
  disableTextHighlighting?: boolean;
  /**
   * Optional className to apply to the highlighted <mark> element.
   */
  highlightProps?: {
    className?: string;
  };
}

/**
 * Custom hook to highlight parts of a label based on a search string.
 * It returns the label with highlighted segments wrapped in <mark> tags.
 *
 * @param label - The label to be highlighted.
 * @param searchString - The string to search for in the label.
 * @param options - Optional parameters to control highlighting behavior.
 * @returns An array of strings and React elements with highlighted segments.
 */
export function useAutocompleteHighlightedLabel(
  label: string,
  searchString?: string,
  options: UseAutocompleteHighlightedLabelOptions = {},
) {
  const { disableTextHighlighting = false, highlightProps = {} } = options;
  const { className } = highlightProps;

  return useMemo(() => {
    if (disableTextHighlighting) {
      return label;
    }
    const parts = highlightLabel(label, searchString);
    return parts.map((part, i) =>
      part.toLowerCase() === (searchString || '').toLowerCase() ? (
        <mark key={i} className={cn('font-semibold bg-transparent text-inherit', className)}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  }, [searchString, label, disableTextHighlighting, className]);
}
